import { MoreVert } from '@mui/icons-material';
import type { TeamTargetResponse } from '@on3/api';
import { getPredictionColor } from '@on3/ui-lib/utils/getPredictionColor';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { format } from 'date-fns';
import Link from 'next/link';
import { useState } from 'react';
import { AthleteType } from 'utils/player';

import styles from './PlayerInterestsItem.module.scss';

interface IPlayerInterestsItemProps extends TeamTargetResponse {
  playerType?: AthleteType;
}

export const PlayerInterestsItem = ({
  team,
  coaches,
  status,
  sport,
  year,
  classRank,
  distance,
  prediction,
  draftPositionCount,
  draftTotal,
  positionKey,
  committedDate,
  playerType = AthleteType.Star,
}: IPlayerInterestsItemProps) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const sluggedSport = slugify(sport?.name || 'football');
  const predictionLevel = prediction
    ? getPredictionColor(prediction)
    : 'level4';
  const league = sluggedSport === 'football' ? 'nfl' : 'nba';
  const recruiters = (coaches ?? []).filter(Boolean);

  return (
    <li
      className={styles.block}
      data-interest-type={playerType.toLowerCase()}
      role="row"
    >
      <div className={styles.team}>
        {team?.asset && (
          <div aria-labelledby="logo" className={styles.avatar} role="cell">
            <Link
              href={`/college/${team?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
            >
              <On3Avatar
                domain={team?.asset?.domain}
                height={25}
                source={team?.asset?.source}
                teamAvatar
                type={team?.asset?.type}
                width={25}
              />
            </Link>
          </div>
        )}

        <div
          aria-labelledby="team"
          className={clsx(styles.name, {
            [styles.noRecruiter]: recruiters.length < 1,
            [styles.noStatus]: !status,
          })}
          role="cell"
        >
          <Link
            href={`/college/${team?.slug}/${sluggedSport}/${year}/industry-comparison-commits/`}
          >
            {team?.name}
          </Link>
        </div>

        {status && (
          <div className={styles.status}>
            <div
              aria-labelledby="status"
              className={styles.statusText}
              data-status={status}
              role="cell"
            >
              {status}
            </div>
            {committedDate && (
              <div
                aria-labelledby="committed-date"
                className={styles.committedDate}
                data-status={committedDate}
                role="cell"
              >
                {format(new Date(committedDate), 'M/dd/yyyy')}
              </div>
            )}
          </div>
        )}

        {playerType !== AthleteType.Base && (
          <div
            aria-labelledby="prediction"
            className={styles.prediction}
            data-level={predictionLevel}
            role="cell"
          >
            {!prediction
              ? 'N/A'
              : `${prediction >= 1 ? prediction?.toFixed(1) : '<1'}%`}
          </div>
        )}

        <div
          aria-labelledby="recruited-by"
          className={styles.coaches}
          role="cell"
        >
          {recruiters.map((coach) => (
            <div className={styles.coach} key={coach.key}>
              <span>{coach.type}</span>
              <Link href={`/rivals/coach/${coach.slug}/`}>{coach.name}</Link>
            </div>
          ))}
        </div>

        <button
          className={styles.more}
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
        >
          <span className="sr-only">More</span>
          <MoreVert />
        </button>
      </div>

      <div
        className={clsx(styles.stats, {
          [styles.hidden]: !isExpanded,
        })}
      >
        <div
          aria-labelledby="distance"
          className={styles.distance}
          data-label="Distance"
          role="cell"
        >
          {distance ? `${distance.toFixed()} mi.` : '-'}
        </div>

        <div
          aria-labelledby="class-rank"
          className={styles.classRank}
          data-label="Class Rank"
          role="cell"
        >
          <Link
            href={`/rivals/rankings/industry-team/${sluggedSport}/${year}/`}
          >
            {classRank ?? '-'}
          </Link>
        </div>

        <div
          aria-labelledby="draft-position"
          className={styles.draftPosition}
          data-label={`${positionKey} Drafted (5yr)`}
          role="cell"
        >
          <Link
            href={`/${league}/draft/${year}/draft-history/draft-by-position/?team-key=${team?.key}&position=${positionKey}&year-span=5`}
          >
            {draftPositionCount ?? '-'}
          </Link>
        </div>

        <div
          aria-labelledby="draft-total"
          className={styles.draftTotal}
          data-label="Total Drafted (5yr)"
          role="cell"
        >
          <Link
            href={`/${league}/draft/${year}/draft-history/draft-by-position/?team-key=${team?.key}&year-span=5`}
          >
            {draftTotal ?? '-'}
          </Link>
        </div>
      </div>
    </li>
  );
};
